<template>
  <div>
    <el-button
      :type="config.type ?? 'text'"
      :link="config.link"
      :plain="config.plain"
      size="small"
      :disabled="disabled"
      @click.stop="handleClick"
    >
      <div class="button-group">
        <i
          v-if="config.icon !== ''"
          class="iconfont text-12"
          :class="config.icon"
        ></i>
        <span class="text-12">{{ config.label }}</span>
      </div>
    </el-button>
  </div>
</template>

<script setup>
import { ref, reactive, watchEffect, onMounted } from "vue";
import { ElMessageBox } from "element-plus";

// 1. 定义组件属性
const props = defineProps({
  type: String,
  label: String,
  icon: String,
  link: {
    type: Boolean,
    default: undefined,
  },
  plain: {
    type: Boolean,
    default: undefined,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  types: String,
});

// 2. 定义事件
const emit = defineEmits(["click"]);

// 3. 响应式配置对象
const config = ref({
  type: "",
  link: false,
  plain: false,
  icon: "",
  label: "按钮",
});

// 4. 预设配置映射
const typeList = reactive({
  add: {
    type: "primary",
    plain: true,
    icon: "icon-tianjia1",
    label: "添加",
    link: false,
  },
  save: {
    icon: "icon-dagou",
    type: "primary",
    label: "保存提交",
    link: false,
  },
  edit: {
    icon: "icon-bianji1",
    type: "primary",
    link: true,
    label: "编辑",
  },
  delete: {
    icon: "icon-guanbi1",
    type: "danger",
    link: true,
    label: "删除",
  },
  stop: {
    icon: "icon-xinxi-2",
    type: "warning",
    link: true,
    label: "禁用",
  },
  open: {
    icon: "icon-dagou-2",
    type: "success",
    link: true,
    label: "启用",
  },
  cancel: {
    icon: "icon-shuaxin1",
    label: "取消",
    plain: false,
    link: false,
  },
  search: {
    icon: "icon-sousuo",
    label: "查询",
    type: "primary",
    plain: true,
  },
  rush: {
    icon: "icon-shuaxin2",
    label: "刷新",
    type: "primary",
    plain: true,
  },
  detail: {
    icon: "icon-gerenka1",
    label: "详情",
    type: "primary",
    link: true,
  },
  default: {
    type: "",
    label: "按钮",
    link: false,
    plain: false,
    disabled: false,
  },
});

// 5. 初始化配置
function buttonInit() {
  const key = props.type && typeList[props.type] ? props.type : "default";
  const presetConfig = { ...typeList[key] };

  // 应用预设配置
  Object.assign(config.value, presetConfig);

  // 覆盖特定属性
  if (props.label) config.value.label = props.label;
  if (props.icon && props.icon !== "") config.value.icon = props.icon;
  // link 属性：如果明确传入（无论是 true 还是 false），都覆盖预设值
  if (props.link !== undefined) config.value.link = props.link;
  if (!props.type && props.types) config.value.type = props.types ?? "";
  // plain 属性：如果明确传入（无论是 true 还是 false），都覆盖预设值
  if (props.plain !== undefined) config.value.plain = props.plain;
}

// 6. 处理点击事件
async function handleClick() {
  if (props.type === "delete") {
    const confirmed = await messageBox("确定删除吗？");
    if (confirmed) emit("click");
  } else if (props.type === "stop") {
    const confirmed = await messageBox("确定禁用吗？");
    if (confirmed) emit("click");
  } else if (props.type === "open") {
    const confirmed = await messageBox("确定启用吗？");
    if (confirmed) emit("click");
  } else {
    emit("click");
  }
}

// 7. 确认对话框
function messageBox(msg) {
  return new Promise((resolve) => {
    ElMessageBox.confirm(msg, "系统提示", {
      confirmButtonText: "确定",
      cancelButtonText: "取消",
      type: "warning",
    })
      .then(() => resolve(true))
      .catch(() => resolve(false));
  });
}

// 8. 初始化并监听属性变化
onMounted(buttonInit);
watchEffect(buttonInit);
</script>

<style lang="scss" scoped>
.button-group {
  display: flex;
  align-items: center;
}
.iconfont {
  margin-right: 3px;
}
</style>
